import React from 'react';
import ReactDOM from 'react-dom';
import { debounce } from 'lodash';
import {
  Editor,
  EditorProps,
  EditorTools,
  EditorUtils,
  ProseMirror,
  EditorChangeEvent
} from '@progress/kendo-react-editor';
import { Error as ErrorLabel } from '@progress/kendo-react-labels';

import {
  AddResourceAsync,
  UpdateGameDocState
} from '../../utils/game-document';
import { tagMark } from '../../utils/new-mark';
import { uuid } from '../../types/common-helper';
import { InsertMedia } from './insert-media-tools';
import { placeholder, styles } from './placeholder';
import { ToggleHtmlView } from './toggle-html-view';
import { Resource } from '../../types/game-document';
import CustomFormatBlock from './custom-format-block';
import { GameDocumentContext } from '../../contexts/game-document';
import { insertImagePlugin, onImageInsert } from './insert-image-plugin';
import { audio, iframe, video, vimeo, youtube } from '../../utils/new-node';

interface EditorInputProps extends EditorProps {
  limit?: number;
  displayCounter?: boolean;
  fieldName?: string;
  handleHtmlChange?: (event: EditorChangeEvent) => void;
  disabled?: boolean;
  resourceCategory?: string;
}

const { imageResizing } = EditorUtils;

export const HtmlEditor = ({ ...props }: EditorInputProps) => {
  const [game, setGame] = React.useContext(GameDocumentContext);

  const [isHtmlView, setIsHtmlView] = React.useState(false);
  const [htmlValue, setHtmlValue] = React.useState(props.value as string);
  const [showImageDropError, setShowImageDropError] = React.useState('');

  const [editorKey, setEditorKey] = React.useState(0);
  const lastEmittedRef = React.useRef<string>((props.value as string) ?? '');
  const skipEmitRef = React.useRef(false);

  const {
    Bold,
    Italic,
    Underline,
    Strikethrough,
    Subscript,
    Superscript,
    AlignLeft,
    AlignCenter,
    AlignRight,
    AlignJustify,
    Indent,
    Outdent,
    OrderedList,
    UnorderedList,
    Undo,
    Redo,
    FontSize,
    FontName,
    Link,
    Unlink,
    InsertTable,
    AddRowBefore,
    AddRowAfter,
    AddColumnBefore,
    AddColumnAfter,
    DeleteRow,
    DeleteColumn,
    DeleteTable,
    MergeCells,
    SplitCell
  } = EditorTools;
  const { Schema } = ProseMirror;

  const onMount = (event: any) => {
    const state = event.viewProps.state;
    const gameId = game.gameId;
    const currentVersion = game.gameDocument?.version;
    const plugins = [
      ...state.plugins,
      insertImagePlugin((args: any) =>
        onImageInsert(
          args,
          gameId ?? 0,
          currentVersion ?? '',
          (newResource: Resource, fileName: string) => {
            const resourceId = uuid();
            AddResourceAsync(
              game.gameDocument!,
              fileName ?? newResource.name,
              fileName ?? newResource.name,
              'image',
              newResource.value,
              resourceId,
              newResource.size,
              props.resourceCategory
            ).then((response) =>
              setGame((prev) => UpdateGameDocState(prev, response))
            );
          },
          (fileName: string, error: unknown) => {
            const message =
              error instanceof Error ? error.message : 'Failed to upload image';
            setShowImageDropError(`${fileName}: ${message}`);
          }
        )
      ),
      imageResizing(),
      placeholder('Your content here...')
    ];
    const { schema } = state;

    const editorDocument = event.dom.ownerDocument;
    const styleElement =
      editorDocument && editorDocument.querySelector('style');
    if (styleElement) {
      styleElement.appendChild(editorDocument.createTextNode(styles));
    }

    // Add the 'dir' attribute to paragraph node.
    const paragraph = {
      ...schema.spec.nodes.get('paragraph')
    };
    paragraph.attrs = paragraph.attrs || {};
    paragraph.attrs['dir'] = {
      default: null
    };
    let nodes = schema.spec.nodes.update('paragraph', paragraph);

    // Append the new node.
    nodes = nodes.addToEnd('iframe', iframe);
    nodes = nodes.addToEnd('video', video);
    nodes = nodes.addToEnd('audio', audio);
    nodes = nodes.addToEnd('youtube', youtube);
    nodes = nodes.addToEnd('vimeo', vimeo);

    // Append a new mark representing the <s> formatting tag.
    const mark = tagMark('s');
    let marks = schema.spec.marks.append(mark);

    // Create the new schema.
    const mySchema = new Schema({
      nodes,
      marks
    });

    // Create a new document using the modified schema.
    let doc = EditorUtils.createDocument(mySchema, (htmlValue as string) ?? '');

    return new ProseMirror.EditorView(
      {
        mount: event.dom
      },
      {
        ...event.viewProps,
        state: ProseMirror.EditorState.create({
          // @ts-ignore
          doc,
          plugins
        })
      }
    );
  };

  const handleHtmlChangeRef = React.useRef(props.handleHtmlChange);
  React.useEffect(() => {
    handleHtmlChangeRef.current = props.handleHtmlChange;
  }, [props.handleHtmlChange]);

  const debouncedHandleHtmlChange = React.useRef(
    debounce((value: string) => {
      lastEmittedRef.current = value;
      handleHtmlChangeRef.current?.({ html: value } as EditorChangeEvent);
    }, 500)
  ).current;

  const onChange = (e: EditorChangeEvent) => {
    setHtmlValue(e.html);
    setShowImageDropError('');
  };

  React.useEffect(() => {
    const incoming = (props.value as string) ?? '';

    if (incoming === lastEmittedRef.current || incoming === htmlValue) return;

    debouncedHandleHtmlChange.cancel();
    lastEmittedRef.current = incoming;
    skipEmitRef.current = true;
    setHtmlValue(incoming);
    setEditorKey((k) => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]);

  const counter = (props.limit ?? 0) - ((htmlValue as string) ?? '').length;
  const editorCounter = (
    <span
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        padding: '3px 10px',
        backgroundColor: counter < 0 ? '#d22630' : '#14bf8b',
        color: 'white'
      }}>
      {counter}
    </span>
  );

  const editor = document.querySelector(
    '.information-players-editor .k-editor-content'
  );

  React.useLayoutEffect(() => {
    if (props.displayCounter && editor) {
      (editor as HTMLDivElement).style.position = 'relative';
    }
  }, [props.displayCounter, editor]);

  // Cancel any pending call on unmount only.
  React.useEffect(() => {
    return () => {
      debouncedHandleHtmlChange.cancel();
    };
  }, [debouncedHandleHtmlChange]);

  const isInitialMount = React.useRef(true);

  React.useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (skipEmitRef.current) {
      skipEmitRef.current = false;
      return;
    }
    debouncedHandleHtmlChange(htmlValue);
  }, [htmlValue, debouncedHandleHtmlChange]);

  return (
    <>
      <Editor
        key={editorKey}
        tools={[
          [Bold, Italic, Underline, Strikethrough],
          [Subscript, Superscript],
          [AlignLeft, AlignCenter, AlignRight, AlignJustify],
          [Indent, Outdent],
          [OrderedList, UnorderedList],
          FontSize,
          FontName,
          CustomFormatBlock,
          [Undo, Redo],
          [
            (pluginProps: any) => (
              <InsertMedia
                {...pluginProps}
                mediaType={'image'}
                resourceCategory={props.resourceCategory}
                resizeMedia
              />
            ),
            (pluginProps: any) => (
              <InsertMedia
                {...pluginProps}
                mediaType={'video'}
                resourceCategory={props.resourceCategory}
                resizeMedia
              />
            ),
            (pluginProps: any) => (
              <InsertMedia
                {...pluginProps}
                mediaType={'audio'}
                resourceCategory={props.resourceCategory}
              />
            )
          ],
          [
            Link,
            Unlink,
            (toolProps: any) => (
              <ToggleHtmlView
                {...toolProps}
                isHtmlView={isHtmlView}
                onToggle={() => setIsHtmlView((prev) => !prev)}
              />
            )
          ],
          [InsertTable],
          [AddRowBefore, AddRowAfter, AddColumnBefore, AddColumnAfter],
          [DeleteRow, DeleteColumn, DeleteTable],
          [MergeCells, SplitCell]
        ]}
        contentStyle={{
          height: 500,
          width: '100%',
          display: isHtmlView ? 'none' : undefined,
          ...(props.displayCounter && {
            paddingTop: '30px'
          }),
          ...(props.disabled && {
            pointerEvents: 'none',
            opacity: 0.6,
            backgroundColor: '#e9ecef'
          })
        }}
        {...props}
        onChange={onChange}
        onMount={onMount}
        value={htmlValue}
      />

      {showImageDropError && <ErrorLabel>{showImageDropError}</ErrorLabel>}

      {isHtmlView && (
        <div style={{ position: 'relative', border: '1px solid #dee2e6' }}>
          {props.displayCounter && editorCounter}
          <textarea
            style={{
              height: '470px',
              width: '100%',
              ...(props.displayCounter && { marginTop: '30px' }),
              border: '0px',
              resize: 'none',
              position: 'relative'
            }}
            disabled={props.disabled}
            className="k-input-inner !k-overflow-auto"
            value={htmlValue}
            onChange={(e) => {
              setHtmlValue(e.target.value);
            }}
          />
        </div>
      )}

      {props.displayCounter &&
        editor &&
        ReactDOM.createPortal(editorCounter, editor)}

      {props.displayCounter && counter < 0 && (
        <span className={'text-danger'}>
          {props.fieldName} may not be longer than {props.limit} characters
        </span>
      )}
    </>
  );
};
